import { useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { companyIdSelector } from "@ryde/shared";

import type { IMapLibrePlaceInfoContext } from "./maplibre-place-info.models";

/** Place info on one map host: whether the map offers it, whether it is on, and what the layers below read. */
export const useMapLibreHostPlaceInfo = ({
  areControlsHidden,
  isPlaceInfoHidden,
}: {
  areControlsHidden: boolean | undefined;
  isPlaceInfoHidden: boolean | undefined;
}) => {
  // The place lookup is company-scoped, so a map outside a company session has nothing to ask.
  const companyId = useRecoilValue(companyIdSelector);
  const isPlaceInfoShown =
    !areControlsHidden && !isPlaceInfoHidden && Boolean(companyId);
  const [isPlaceInfoActive, setIsPlaceInfoActive] = useState(false);
  /*
   * While place info is on, every map click is a place lookup. A click inside its popup still reaches
   * MapLibre's click listener, so `onMapClick` (move the address, pick a station) is paused meanwhile.
   */
  const isPickingPlace = isPlaceInfoShown && isPlaceInfoActive;

  const endPlaceInfo = useEvent((): void => setIsPlaceInfoActive(false));

  const placeInfoContext = useMemo(
    () =>
      ({ isPickingPlace, endPlaceInfo }) satisfies IMapLibrePlaceInfoContext,
    [isPickingPlace, endPlaceInfo],
  );

  return {
    isPlaceInfoShown,
    isPlaceInfoActive,
    setIsPlaceInfoActive,
    isPickingPlace,
    placeInfoContext,
  };
};
